import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { Attachment } from '@/lib/types';
import type { AxonEvent } from '@/lib/types';
import type { AgentStreamPageInfo } from '@reflex/shared';
import { TOP_PROGRESS } from '@/components/layout/top-progress-store';
import {
  mergeEarlierEvents,
  mergeEarlierPageInfo,
  mergeFreshWindow,
  normalizeStreamResponse,
} from './stream-paging';
import { useEffect, useMemo } from 'react';

/**
 * The agent's event stream, last-turn-first: the request opts into the
 * server's paged window, and the response shape decides the mode. An
 * indexed agent answers with `{ events, page }` — the query data is the
 * window's events and the page info lands under
 * `queryKeys.streamPage(agentId)` for the socket hook and the
 * load-earlier UI. A legacy (unindexed) agent answers with the full
 * array and a `null` page — behavior identical to before paging existed.
 *
 * The cache shape stays a flat `AxonEvent[]` either way, so every
 * downstream consumer (reducers, activity derivation, socket appends) is
 * unchanged.
 *
 * This is the one definition of the windowed stream query — key, fetch,
 * and the `streamPage` cache write — shared by the mounted hook and the
 * page-level prefetch so the two are always the same query and
 * react-query dedupes them into one request.
 */
function streamQueryOptions(queryClient: QueryClient, agentId: string) {
  return {
    // The transcript is what the user navigated to see — its initial load is
    // the page loading, so it drives the global top progress bar.
    meta: { [TOP_PROGRESS]: true },
    queryKey: [...queryKeys.stream(agentId)],
    queryFn: async () => {
      const { events, page } = normalizeStreamResponse(await api.getStreamPaged(agentId));
      queryClient.setQueryData<AgentStreamPageInfo | null>(
        [...queryKeys.streamPage(agentId)],
        page,
      );
      return events;
    },
    staleTime: Infinity,
  } as const;
}

export function useAgentStream(agentId: string | null) {
  const queryClient = useQueryClient();
  return useQuery({
    ...streamQueryOptions(queryClient, agentId ?? ''),
    enabled: !!agentId,
  });
}

/**
 * Fire the initial window fetch as soon as the URL names the agent,
 * without waiting for the agent row to resolve — the session page mounts
 * {@link useAgentStream} only after the agent query lands, which used to
 * cost the transcript a full round-trip of queueing. Same query as the
 * hook, so the mount dedupes onto the in-flight request. `prefetchQuery`
 * swallows errors by design: a prefetch for a 404/denied agent fails
 * silently, exactly like the no-request world before it (the mount gate
 * keeps the stream hook from ever observing those agents), and the
 * mounted hook keeps its own retry/error behavior.
 */
export function usePrefetchAgentStream(agentId: string | null | undefined) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!agentId) return;
    void queryClient.prefetchQuery(streamQueryOptions(queryClient, agentId));
  }, [agentId, queryClient]);
}

/**
 * The agent's complete event history, independent of the paged window: a
 * request with no paging params makes the server answer with the full
 * array regardless of indexing. For consumers whose semantics need every
 * event (plugin tabs, boot-time dev-server discovery, alert markers) —
 * the chat transcript stays on the windowed {@link useAgentStream}.
 */
export function useAgentStreamFull(agentId: string | null) {
  return useQuery({
    queryKey: [...queryKeys.streamFull(agentId ?? '')],
    queryFn: async () => normalizeStreamResponse(await api.getStream(agentId!)).events,
    enabled: !!agentId,
    staleTime: Infinity,
  });
}

/**
 * Full history plus the live tail. The one-shot full fetch cannot see
 * events that arrive after it resolves, so the windowed `stream` cache —
 * which the socket appends to — is folded on top of it. For legacy
 * agents the windowed cache *is* the full history and is returned as-is
 * (no second fetch).
 */
export function useAgentStreamFullHistory(agentId: string | null): AxonEvent[] | undefined {
  const { data: windowed } = useAgentStream(agentId);
  const page = useAgentStreamPage(agentId);
  const full = useAgentStreamFull(page ? agentId : null);
  return useMemo(
    () => (page ? mergeFreshWindow(full.data ?? [], windowed ?? []) : windowed),
    [page, full.data, windowed],
  );
}

/**
 * The loaded window's page info. `undefined` until the initial stream
 * fetch resolves; `null` for legacy agents (full history loaded, nothing
 * to page). Written only by {@link useAgentStream} and
 * {@link useLoadEarlierEvents} — this query never fetches on its own.
 */
export function useAgentStreamPage(agentId: string | null): AgentStreamPageInfo | null | undefined {
  const { data } = useQuery<AgentStreamPageInfo | null>({
    queryKey: [...queryKeys.streamPage(agentId ?? '')],
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
  });
  return data;
}

/**
 * Fetch the page before the loaded window and prepend it into the stream
 * cache. No-op when the agent is legacy or the watermark floor has been
 * reached.
 */
export function useLoadEarlierEvents(agentId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!agentId) return;
      const pageKey = [...queryKeys.streamPage(agentId)];
      const page = queryClient.getQueryData<AgentStreamPageInfo | null>(pageKey);
      if (!page?.hasMore || page.prevCursor === null) return;
      const response = await api.getStreamPaged(agentId, page.prevCursor);
      const { events, page: earlier } = normalizeStreamResponse(response);
      // A legacy-shaped response to a `before` request means the server has
      // left paged mode (pagination switched off mid-session). Nothing sane
      // to prepend, and no cursor protocol left to continue — close paging
      // so no caller (the load-earlier UI, a jump) re-requests forever.
      if (!earlier) {
        queryClient.setQueryData<AgentStreamPageInfo | null>(pageKey, (old) =>
          old ? { ...old, hasMore: false, prevCursor: null } : old,
        );
        return;
      }
      queryClient.setQueryData<AxonEvent[]>([...queryKeys.stream(agentId)], (old) =>
        mergeEarlierEvents(old ?? [], events),
      );
      queryClient.setQueryData<AgentStreamPageInfo | null>(pageKey, (old) =>
        old ? mergeEarlierPageInfo(old, earlier) : earlier,
      );
    },
  });
}

export function useSendMessage() {
  return useMutation({
    // Deliberately NOT opted out: sending a prompt is the primary action of
    // the page and the user is waiting on it, so it drives the global top
    // progress bar — even though the transcript also shows an optimistic
    // bubble while the provider accepts the turn.
    mutationFn: ({
      agentId,
      message,
      attachments,
    }: {
      agentId: string;
      message: string;
      attachments?: Attachment[];
    }) => api.sendMessage(agentId, message, attachments),
  });
}
